import {
  CASH_CROP_HARVEST_TYPES,
  CASH_CROP_SPECIES,
  RDCashCrop,
  RDCashCropInput,
  RDCashCropSpecies,
} from '@cibo/core'
import dayjs from 'dayjs'
import { propEq } from 'ramda'
import {
  DateSchema,
  ObjectSchema,
  StringSchema,
  boolean,
  date,
  mixed,
  number,
  object,
  ref,
  string,
} from 'yup'
import { DetailEditorProps, ITraitFeatureByIdYear } from '../types'
import { isDetailRequired } from '../useIsRequired'
import { findInitialValuesDetail } from '../utils'
import { CashCropCell } from './CashCropCell'
import { CashCropCellEditor } from './CashCropCellEditor'
import { CashCropDetailSimpleCell } from './CashCropDetailSimpleCell'
import { CashCropEditor } from './CashCropEditor'
import { CashCropResultsOnlyEditor } from './CashCropResultsOnlyEditor'
import { isFallow } from './isFallow'

const TRAIT_ID = 'cashCrop'

export const CashCropResult: ITraitFeatureByIdYear<RDCashCrop> = {
  rollups: {},
  forbidBulkEdit: false,
  traitId: TRAIT_ID,
  editor: CashCropResultsOnlyEditor,
  cellDisplay: CashCropCell,
  SimpleCellView: CashCropDetailSimpleCell,
  ns: '@cibo/landmanager/CashCropEditor',
  validationSchema: (props: Pick<DetailEditorProps, 'requirement'>) =>
    string().oneOf(CASH_CROP_SPECIES).nullable() as StringSchema<RDCashCrop['result']>,

  initialValues: ({ fieldModels, year }) => {
    const cashCrops =
      fieldModels.length === 1
        ? fieldModels[0].details.filter(propEq('traitId', 'cashCrop'))
        : undefined
    return cashCrops?.filter(d => d.year === year)?.[0]?.result
  },
  cellEditor: CashCropCellEditor,
  detailFromEditorValues: (formValues, requirement) => ({
    traitId: TRAIT_ID,
    year: requirement?.year,
    result: formValues.input,
  }),
}

export const CashCropInput: ITraitFeatureByIdYear<RDCashCrop> = {
  rollups: {},
  forbidBulkEdit: false,
  traitId: TRAIT_ID,
  editor: CashCropEditor,
  cellDisplay: CashCropCell,
  SimpleCellView: CashCropDetailSimpleCell,
  ns: '@cibo/landmanager/CashCropEditor',
  validationSchema: ({ requirement, t }) => {
    return object({
      crop: string<RDCashCropSpecies>().required(t('required')),
      intendedCrop: string<RDCashCropSpecies>().when('crop', {
        is: 'preventPlant',
        then: (schema: any) => schema.required(t('required')),
      }),
      preventPlant: boolean().nullable(),
      acres: number().min(0).nullable(),
      plantedAt: date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .when('crop', {
          is: isFallow,
          then: (schema: any) => schema.nullable(),
          otherwise: isDetailRequired({ requirement, fieldName: 'plantedAt' })
            ? (schema: DateSchema) =>
                schema.when('harvestedAt', ([harvestedAt], sch) => {
                  const isValid = harvestedAt && !isNaN(harvestedAt?.valueOf())
                  return isValid
                    ? sch
                        .test(
                          'dateRequired',
                          t('required_fourDigitYear'),
                          // @ts-ignore typing doesn't like this.
                          (date: string) => dayjs(date).year() > 1969
                        )
                        .required(t('required', { context: 'plantedAt' }))
                        .typeError(t('required', { context: 'plantedAt' }))
                    : sch
                        .max(ref('harvestedAt'), ({ max }) =>
                          t('max', { context: 'plantedAt', max })
                        )
                        .required(t('required', { context: 'plantedAt' }))
                        .typeError(t('required', { context: 'plantedAt' }))
                })
            : (schema: any) =>
                schema
                  .nullable()
                  .typeError(t('invalid', { context: 'plantedAt' }))
                  .when('harvestedAt', {
                    is: (val: Date | undefined) => !val || isNaN(val?.valueOf()),
                    then: (schema: any) => schema.nullable(),
                    otherwise: (schema: any) =>
                      schema.max(ref('harvestedAt'), ({ max }: { max: number }) =>
                        t('max', { context: 'plantedAt', max })
                      ),
                  }),
        }),
      harvestedAt: date()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .when('crop', {
          is: isFallow,
          then: (schema: any) => schema.nullable(),
          otherwise: isDetailRequired({ requirement, fieldName: 'harvestedAt' })
            ? (schema: DateSchema) =>
                schema.when('plantedAt', ([plantedAt], sch) =>
                  !plantedAt || isNaN(plantedAt?.valueOf())
                    ? sch
                        .test(
                          'dateRequired',
                          t('required_fourDigitYear'),
                          // @ts-ignore typing doesn't like this.
                          (date: string) => dayjs(date).year() > 1969
                        )
                        .required(
                          t('required', {
                            context: 'harvestedAt',
                          })
                        )
                        .typeError(
                          t('required', {
                            context: 'harvestedAt',
                          })
                        )
                    : sch
                        .min(ref('plantedAt'), ({ min }) =>
                          t('min', {
                            context: 'harvestedAt',
                            min,
                          })
                        )
                        .required(
                          t('required', {
                            context: 'harvestedAt',
                          })
                        )
                        .typeError(
                          t('required', {
                            context: 'harvestedAt',
                          })
                        )
                )
            : (schema: any) =>
                schema
                  .nullable()
                  .typeError(
                    t('invalid', {
                      context: 'harvestedAt',
                    })
                  )
                  .when('plantedAt', {
                    is: (val: Date | undefined) => !val || isNaN(val?.valueOf()),
                    then: (schema: any) => schema.nullable(),
                    otherwise: (schema: any) =>
                      schema.min(ref('plantedAt'), ({ min }: { min: number }) =>
                        t('min', {
                          context: 'harvestedAt',
                          min,
                        })
                      ),
                  }),
        }),
      harvestType: string().when('crop', {
        is: isFallow,
        then: (schema: any) => schema.nullable(),
        otherwise: isDetailRequired({ requirement, fieldName: 'plantingRate' })
          ? (schema: any) =>
              schema
                .oneOf(CASH_CROP_HARVEST_TYPES)
                .required(t('required', { context: 'harvestType' }))
                .typeError(t('required', { context: 'harvestType' }))
          : (schema: any) => schema.nullable(),
      }),
      yieldPerAcre: mixed().when('crop', ([crop], schema) => {
        return isFallow(crop)
          ? schema.nullable()
          : string().when('harvestType', ([harvestType], schema) =>
              ['grazed', 'none', 'not_available'].includes(harvestType)
                ? schema.nullable()
                : isDetailRequired({ requirement, fieldName: 'yieldPerAcre' })
                ? schema.required(t('required'))
                : schema.nullable()
            )
      }),
      residuePercentageRemoved: number().min(0).max(100).nullable(),
      plantingRate: mixed().when('crop', {
        is: isFallow,
        then: (schema: any) => schema.nullable(),
        otherwise: isDetailRequired({ requirement, fieldName: 'plantingRate' })
          ? (schema: any) => schema.number().required(t('required'))
          : (schema: any) => schema.nullable(),
      }),
      plantingRateUnit: string().when('crop', {
        is: isFallow,
        then: (schema: any) => schema.nullable(),
        otherwise: isDetailRequired({ requirement, fieldName: 'plantingRateUnit' })
          ? (schema: any) => schema.oneOf(['seedsPerAcre', 'poundsPerAcre']).required(t('required'))
          : (schema: any) => schema.nullable(),
      }),
      herbicideBurndown: boolean().when('crop', ([crop], schema) => {
        return isFallow(crop)
          ? schema.nullable()
          : isDetailRequired({ requirement, fieldName: 'herbicideBurndown' })
          ? schema.required(t('required'))
          : schema.nullable()
      }),
      fireBurndown: boolean().when('crop', ([crop], schema) => {
        return isFallow(crop)
          ? schema.nullable()
          : isDetailRequired({ requirement, fieldName: 'fireBurndown' })
          ? schema.required(t('required'))
          : schema.nullable()
      }),
    }) as ObjectSchema<RDCashCrop['input']>
  },

  initialValues: request => {
    const detail = findInitialValuesDetail('cashCrop')(request)

    if (!detail) {
      return {
        plantedAt: null,
        harvestedAt: null,
      }
    }

    if (!detail.input) {
      // @ts-ignore MUI's DatePicker prefers empty string to undefined
      return {
        crop: detail.result,
        plantedAt: null,
        harvestedAt: null,
        source: detail.source,
      } as RDCashCrop['input']
    }

    if (!detail.input.crop) {
      // becasue of how cropnosis returns results, it is possible that we have planting and harvest dates
      // but not a crop value. this prefills crop value with the result, then ensures the dates are present
      // then spreads the input value into the form to cover all our bases
      return {
        crop: detail?.result,
        plantedAt: null,
        harvestedAt: null,
        source: detail.source,
        ...detail?.input,
      }
    }

    const cropValueWithPreventPlant = detail.input.preventPlant
      ? ({ crop: 'preventPlant', intendedCrop: detail.input.crop } as Partial<RDCashCropInput>)
      : { crop: detail.input.crop }

    return {
      plantedAt: null,
      harvestedAt: null,
      source: detail.source,
      ...detail.input,
      ...cropValueWithPreventPlant,
    }
  },
  detailFromEditorValues: (formValues, requirement) => {
    const { source, intendedCrop, ...inputRest } = formValues.input
    const input =
      inputRest.crop === 'preventPlant'
        ? { ...inputRest, crop: intendedCrop, preventPlant: true }
        : inputRest
    return {
      traitId: TRAIT_ID,
      year: requirement?.year,
      input,
    }
  },
}
